/** /screen/:code — the projector view. Always dark, large type, no interaction. */
import { storageKey } from "@shared/brand";
import { formatNumber } from "@shared/i18n";
import type { JoinResponse, ServerEvent } from "@shared/protocol";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import {
  ArtifactCard,
  Dimensions,
  Leaderboard,
  PhoneCall,
  Radar,
  ReactionsLayer,
  TimerRing,
  VoteBars,
} from "../components/game";
import { Icon } from "../components/Icon";
import { QrCode } from "../components/QrCode";
import { Button, Field, QualityPill, Spinner } from "../components/ui";
import { useI18n } from "../i18n";
import { type ApiError, post } from "../lib/api";
import { useRoom, useServerClock } from "../lib/room";
import {
  type PlayerScenario,
  type RevealCall,
  type RevealDecision,
  useReveal,
  useScenario,
} from "../lib/scenario";

interface ScreenCreds {
  token: string;
  wsPath: string;
}

function loadCreds(code: string): ScreenCreds | null {
  try {
    const raw = sessionStorage.getItem(storageKey(`screen.${code}`));
    return raw ? (JSON.parse(raw) as ScreenCreds) : null;
  } catch {
    return null;
  }
}

export function Screen({ code, query }: { code: string; query: URLSearchParams }) {
  const { t } = useI18n();
  const [creds, setCreds] = useState<ScreenCreds | null>(() => loadCreds(code));
  const [key, setKey] = useState(query.get("k") ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = "dark";
    document.documentElement.classList.add("screen-mode");
    return () => {
      document.documentElement.classList.remove("screen-mode");
    };
  }, []);

  useEffect(() => {
    if (creds || !key) return;
    post<JoinResponse>(`/api/join/${code}/screen`, { key })
      .then((r) => {
        const c = { token: r.token, wsPath: r.wsPath };
        sessionStorage.setItem(storageKey(`screen.${code}`), JSON.stringify(c));
        setCreds(c);
        // Drop the key from the URL bar so it is not shown on the projector.
        history.replaceState(null, "", `/screen/${code}`);
      })
      .catch((e: ApiError) => setError(e.status === 403 ? t("screen.forbidden") : t("error.generic")));
  }, [code, key, creds]);

  if (!creds) {
    return (
      <div class="screen-root" style={{ display: "grid", placeItems: "center" }}>
        <form
          class="card stack"
          style={{ maxWidth: 480 }}
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            setKey((document.getElementById("skey") as HTMLInputElement).value.trim());
          }}
        >
          <h1>{t("screen.unlock")}</h1>
          <Field label={t("screen.unlock")} help={t("screen.unlockHelp")} htmlFor="skey" error={error}>
            <input id="skey" class="input mono" autoComplete="off" />
          </Field>
          <Button type="submit">{t("action.continue")}</Button>
        </form>
      </div>
    );
  }
  return <ScreenLive code={code} creds={creds} />;
}

/**
 * Tracks whether `.screen-main` has more content below the fold, so the fade and the "more below"
 * chevron in pages.css only appear when that is true — the affordance is only as honest as this
 * measurement. `recompute` is cheap (it only reads scroll geometry), so the caller re-runs it after
 * every render that might have changed the content, in addition to this hook's own ResizeObserver
 * (the box itself resizing, e.g. the browser window) and scroll listener (the facilitator scrolling
 * manually). The ref callback also focuses the element once, the first time it mounts, so PageDown
 * and the arrow keys scroll it from the presenting laptop without a click — Chromium shows a
 * focus ring for a script-initiated focus on a freshly loaded page regardless of `tabIndex`, the
 * same as a form's `autofocus` would, so `data-kbd` gates the ring in pages.css to only after a
 * real keypress on the element, not the silent auto-focus every session opens with.
 */
function useMoreBelow() {
  const elRef = useRef<HTMLElement | null>(null);
  const detach = useRef<(() => void) | null>(null);
  const recompute = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    const more = el.scrollHeight - el.scrollTop - el.clientHeight > 2;
    if (el.dataset.more !== String(more)) el.dataset.more = String(more);
  }, []);
  // The listeners are attached from the ref callback, not from an effect: the element mounts only
  // once the room state has arrived (a spinner renders first), which is after the mount effect has
  // already run, so an effect keyed on the ref would never see it.
  const ref = useCallback(
    (el: HTMLElement | null) => {
      detach.current?.();
      detach.current = null;
      elRef.current = el;
      if (!el) return;
      const markKeyboardUse = () => {
        el.dataset.kbd = "true";
      };
      const ro = new ResizeObserver(recompute);
      ro.observe(el);
      el.addEventListener("scroll", recompute, { passive: true });
      el.addEventListener("keydown", markKeyboardUse);
      detach.current = () => {
        ro.disconnect();
        el.removeEventListener("scroll", recompute);
        el.removeEventListener("keydown", markKeyboardUse);
      };
      recompute();
      el.focus({ preventScroll: true });
    },
    [recompute],
  );
  useEffect(() => () => detach.current?.(), []);
  return { ref, recompute };
}

function ScreenLive({ code, creds }: { code: string; creds: ScreenCreds }) {
  const { t, locale } = useI18n();
  const room = useRoom(creds.wsPath);
  const { state, status } = room;
  const contentLocale = state?.settings.locale ?? locale;
  const { data } = useScenario<PlayerScenario>(state?.scenarioId ?? null, contentLocale, "player", code);
  const scenario = data?.scenario ?? null;
  const { ref: mainRef, recompute: recomputeMore } = useMoreBelow();
  // No dependency array: this recomputes after every render, since almost any change to `state`
  // (a new node, a vote landing, the reveal, a resize) can change how much of `.screen-main`
  // overflows, and the recompute itself is just two geometry reads.
  useLayoutEffect(() => {
    recomputeMore();
  });
  const [reactionSubs] = useState(() => new Set<(e: string) => void>());
  const subscribe = useCallback(
    (fn: (e: string) => void) => {
      reactionSubs.add(fn);
      return () => {
        reactionSubs.delete(fn);
      };
    },
    [reactionSubs],
  );
  const [banner, setBanner] = useState<string | null>(null);
  useEffect(
    () =>
      room.onEvent((e: ServerEvent) => {
        if (e.type === "reaction") for (const fn of reactionSubs) fn(e.reaction.emoji);
        if (e.type === "toast" && e.key === "toast.inject") {
          setBanner(t("screen.injectFired", { title: String(e.params?.title ?? "") }));
          setTimeout(() => setBanner(null), 6000);
        }
      }),
    [room, reactionSubs, t],
  );

  const joinUrl = `${location.origin}/j/${code}`;
  const nodeId = state?.nodeId ?? null;
  const node = nodeId && scenario ? scenario.nodes[nodeId] : null;
  const voteOpen = Boolean(state?.vote && !state.vote.result);
  const now = useServerClock(room, voteOpen || Boolean(state?.poll && !state.poll.closed), 200);
  const revealed = Boolean(state?.vote?.result && state.vote.nodeId === nodeId);
  const callShown = node?.type === "call" && state !== null && state.revealedLines >= node.transcript.length;
  const reveal = useReveal(code, nodeId, creds.token, contentLocale, revealed || callShown);

  if (!state || !scenario) {
    return (
      <div class="screen-root" style={{ display: "grid", placeItems: "center" }}>
        <Spinner label={status === "open" ? undefined : t("status.connecting")} />
      </div>
    );
  }

  const players = state.players;
  // The lobby's main view already shows the QR code, join code, URL and player count at room size, so the
  // sidebar would only repeat them; once live, the facilitator can switch it off to give the stage the
  // full width.
  const showSidebar = state.status !== "lobby" && state.settings.showSidebar;
  const sidebar = (
    <aside class="screen-side">
      <div class="screen-join">
        <QrCode value={joinUrl} size={200} label={t("screen.join.scan")} />
        <div>
          <div class="eyebrow">{t("screen.join.code")}</div>
          <div class="code-display screen-code">{code}</div>
          <div class="small" style={{ color: "var(--fg-muted)" }}>
            {t("screen.join.or")}{" "}
            <span class="mono">
              {location.host}/j/{code}
            </span>
          </div>
        </div>
      </div>
      <div class="screen-stat">
        <Icon name="users" /> {t("screen.players", { count: state.playerCount })}
        {state.viewerCount ? (
          <span class="muted"> · {t("screen.viewers", { count: state.viewerCount })}</span>
        ) : null}
      </div>
      {state.status !== "lobby" && state.settings.showLeaderboard && players.length ? (
        <div class="stack-sm">
          <div class="eyebrow">{t("screen.leaderboard")}</div>
          <Leaderboard players={players} roles={scenario.roles} limit={8} />
        </div>
      ) : null}
      {state.status !== "lobby" ? (
        <div class="stack-sm">
          <div class="eyebrow">{t("screen.readiness")}</div>
          <div class="mono" style={{ fontSize: "3rem", fontWeight: 700, lineHeight: 1 }}>
            {state.readiness ?? "—"}
          </div>
          <Dimensions dimensions={scenario.dimensions} scores={state.dimensionScores} />
        </div>
      ) : null}
    </aside>
  );

  let main: preact.ComponentChildren = null;
  if (state.status === "lobby") {
    main = (
      <div class="screen-lobby">
        <div class="eyebrow">{t("status.lobby")}</div>
        <h1 class="screen-title">{state.title}</h1>
        <p class="screen-lead">{scenario.tagline}</p>
        <div class="screen-join big">
          <QrCode value={joinUrl} size={320} label={t("screen.join.scan")} />
          <div>
            <div class="eyebrow">{t("screen.join.title")}</div>
            <div class="code-display screen-code xl">{code}</div>
            <div class="screen-lead mono" style={{ fontSize: "1.4rem" }}>
              {location.host}/j/{code}
            </div>
            <div class="screen-stat" style={{ marginTop: 16 }}>
              <Icon name="users" /> {t("screen.players", { count: state.playerCount })}
            </div>
          </div>
        </div>
        <div class="screen-avatars" aria-hidden="true">
          {players.slice(0, 60).map((p) => (
            <span key={p.id} class="fade-in" title={p.name}>
              {p.avatar}
            </span>
          ))}
        </div>
        {!state.facilitatorConnected ? <p class="muted">{t("screen.waitingHost")}</p> : null}
      </div>
    );
  } else if (state.status === "debrief" || state.status === "ended") {
    main = (
      <div class="stack-lg">
        <div>
          <div class="eyebrow">{t("screen.debrief.title")}</div>
          <h1 class="screen-title">{state.title}</h1>
        </div>
        <div class="screen-debrief">
          <Radar dimensions={scenario.dimensions} scores={state.dimensionScores} size={420} />
          <div class="stack">
            <div>
              <div class="eyebrow">{t("screen.readiness")}</div>
              <div class="mono" style={{ fontSize: "6rem", fontWeight: 800, lineHeight: 1 }}>
                {state.readiness ?? "—"}
              </div>
            </div>
            {state.teamBadges.length ? (
              <div class="stack-sm">
                <div class="eyebrow">{t("screen.badges.team")}</div>
                <div class="row">
                  {state.teamBadges.map((b) => {
                    const def = scenario.badges.find((x) => x.id === b);
                    return (
                      <span key={b} class="pill accent" style={{ fontSize: "1.1rem", minHeight: 40 }}>
                        <Icon name={def?.icon ?? "award"} /> {def?.name ?? b}
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : null}
            {node?.type === "debrief" ? (
              <p class="screen-lead" style={{ whiteSpace: "pre-line" }}>
                {node.body}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  } else if (state.paused) {
    main = (
      <div class="screen-center">
        <Icon name="pause" class="lg" />
        <h1 class="screen-title">{t("screen.paused")}</h1>
      </div>
    );
  } else if (node) {
    switch (node.type) {
      case "narrative":
        main = (
          <div class="stack-lg fade-in" key={nodeId}>
            <div class="eyebrow">{node.title}</div>
            <p class="screen-body" style={{ whiteSpace: "pre-line" }}>
              {node.body}
            </p>
            {node.artifact ? (
              <div class="screen-artifact">
                <ArtifactCard artifact={node.artifact} />
              </div>
            ) : null}
          </div>
        );
        break;
      case "call":
        main = (
          <div class="stack fade-in screen-call" key={nodeId}>
            <div class="eyebrow">{node.title}</div>
            <PhoneCall
              node={node}
              revealedLines={state.revealedLines}
              reveal={reveal?.type === "call" ? (reveal as RevealCall) : null}
            />
          </div>
        );
        break;
      case "reveal":
        main = (
          <div class="stack-lg fade-in" key={nodeId}>
            <div class="eyebrow">{t("player.reveal.title")}</div>
            <h1 class="screen-title">{node.title}</h1>
            <p class="screen-body" style={{ whiteSpace: "pre-line" }}>
              {node.body}
            </p>
            {node.lessons.length ? (
              <div>
                <div class="eyebrow">{t("screen.lessons")}</div>
                <ul class="screen-list">
                  {node.lessons.map((l, i) => (
                    <li key={i}>{l}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        );
        break;
      case "decision": {
        const vote = state.vote?.nodeId === nodeId ? state.vote : null;
        const decisionReveal = reveal?.type === "decision" ? (reveal as RevealDecision) : null;
        const bars = node.options.map((o) => ({
          id: o.id,
          label: o.label,
          quality: decisionReveal?.options.find((x) => x.id === o.id)?.quality,
        }));
        main = (
          <div class="stack-lg" key={nodeId}>
            <div class="row between" style={{ alignItems: "flex-start" }}>
              <div class="grow">
                <div class="eyebrow">{node.title}</div>
                <h1 class="screen-title">{node.prompt}</h1>
                {node.context ? <p class="screen-lead">{node.context}</p> : null}
              </div>
              {vote && !vote.result ? (
                <TimerRing closesAt={vote.closesAt} durationSec={vote.durationSec} now={now} size={180} />
              ) : null}
            </div>
            {vote?.result ? (
              <div class="stack fade-in">
                <div class="row">
                  <span class="eyebrow">{t("screen.vote.result")}</span>
                  {decisionReveal ? (
                    <QualityPill
                      quality={decisionReveal.options.find((o) => o.id === vote.result!.optionId)!.quality}
                    />
                  ) : null}
                </div>
                <h2 class="screen-choice">
                  {node.options.find((o) => o.id === vote.result!.optionId)?.label}
                </h2>
                {decisionReveal ? (
                  <p class="screen-lead">
                    {decisionReveal.options.find((o) => o.id === vote.result!.optionId)?.feedback}
                  </p>
                ) : null}
                <div class="screen-bars">
                  <VoteBars options={bars} tally={vote.tally} winnerId={vote.result.optionId} showQuality />
                </div>
              </div>
            ) : (
              <div class="stack">
                <div class="screen-options">
                  {node.options.map((o, i) => (
                    <div key={o.id} class="screen-option">
                      <span class="screen-option-n">{String.fromCharCode(65 + i)}</span>
                      <span>{o.label}</span>
                    </div>
                  ))}
                </div>
                <div class="row" style={{ fontSize: "1.4rem" }}>
                  <span class="pill live" style={{ fontSize: "1rem" }}>
                    {t("screen.vote.open")}
                  </span>
                  {vote ? (
                    <span>
                      {t("screen.vote.progress", { cast: vote.votesCast, eligible: vote.eligible })}
                    </span>
                  ) : null}
                  {vote && !state.settings.liveTally ? (
                    <span class="muted">· {t("screen.vote.hidden")}</span>
                  ) : null}
                </div>
                {vote && state.settings.liveTally ? (
                  <div class="screen-bars">
                    <VoteBars options={bars} tally={vote.tally} />
                  </div>
                ) : vote ? (
                  <div class="screen-dots" aria-hidden="true">
                    {players.map((p) => (
                      <span key={p.id} class={vote.votedPlayerIds.includes(p.id) ? "on" : ""}>
                        {p.avatar}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
        break;
      }
      case "poll": {
        const poll = state.poll?.nodeId === nodeId ? state.poll : null;
        main = (
          <div class="stack-lg" key={nodeId}>
            <div class="row between" style={{ alignItems: "flex-start" }}>
              <div class="grow">
                <div class="eyebrow">{t("screen.poll.title")}</div>
                <h1 class="screen-title">{node.prompt}</h1>
              </div>
              {poll && !poll.closed ? (
                <TimerRing closesAt={poll.closesAt} durationSec={node.timerSec} now={now} size={160} />
              ) : null}
            </div>
            <div class="screen-bars">
              <VoteBars options={node.options} tally={poll?.tally ?? {}} />
            </div>
            {poll ? <p class="screen-lead">{formatNumber(contentLocale, poll.votesCast)}</p> : null}
          </div>
        );
        break;
      }
      default:
        main = <Spinner />;
    }
  }

  return (
    <div class="screen-root" lang={contentLocale}>
      {state.settings.reactions ? <ReactionsLayer subscribe={subscribe} /> : null}
      {banner ? (
        <div class="screen-banner" role="status">
          <Icon name="zap" /> {banner}
        </div>
      ) : null}
      <div class={showSidebar ? "screen-grid" : "screen-grid solo"}>
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region that has no other
            focusable content still needs to be reachable, so PageDown and the arrow keys can
            scroll it from the presenting laptop — the standard pattern for a scroll container. */}
        <main class="screen-main" ref={mainRef} tabIndex={0} aria-label={t("screen.scrollable")}>
          {main}
          <span class="screen-more" aria-hidden="true">
            <Icon name="chevron-down" />
          </span>
        </main>
        {showSidebar ? sidebar : null}
      </div>
      {status !== "open" ? (
        <div class="screen-conn" role="alert">
          <Icon name="wifi-off" /> {t("status.reconnecting")}
        </div>
      ) : null}
    </div>
  );
}
